import { useEffect, useMemo, useState, type FC } from "react";
import { Skeleton } from "primereact/skeleton";
import ChartSwitcher from "@/components/ChartSwitcher";
import useDarkMode from "@/hooks/useDarkMode";
import {
  buildGeoMapOption,
  buildTopCitiesBarOption,
} from "@/pages/CampaignOverviewPage/charts/geoChartOptions";
import { pillBarChartHeightRem } from "@/pages/CampaignOverviewPage/charts/pillBarChart";
import EChart from "@/pages/CampaignOverviewPage/components/EChart";
import GeoGrid from "@/pages/CampaignOverviewPage/widgets/GeoGrid";
import { registerUsaMap } from "@/pages/CampaignOverviewPage/charts/registerUsaMap";
import {
  cityLabel,
  collectCities,
  deriveRegionCities,
  deriveRegionTotals,
  deriveTopCities,
  deriveTopMarketCards,
} from "@/pages/CampaignOverviewPage/utils/geoDerivation";
import type { PerformanceReportRow } from "@/types/reporting";
import { TEXT } from "@/constants/text";
import {
  COMPACT_NUMBER_FORMATTER,
  CSV_ESCAPE,
  fmtPct,
} from "@/utils/tableFormatters";
import styles from "@/pages/CampaignOverviewPage/widgets/GeoWidget.module.css";

interface GeoWidgetProps {
  error: string | null;
  isLoading: boolean;
  rows: PerformanceReportRow[];
}

// Module-level guard so registerUsaMap() runs exactly once across all renders
// and is deferred to useEffect — keeping it out of module scope prevents test
// crashes when echarts/topojson aren't available in the Jest environment.
let geoMapRegistered = false;

const GEO_TEXT = TEXT.campaignOverview.geo;

const VIEW_MODE_OPTIONS = [
  { key: "map", label: GEO_TEXT.viewMap },
  { key: "grid", label: GEO_TEXT.viewGrid },
] as const;

const GeoWidget: FC<GeoWidgetProps> = ({ error, isLoading, rows }) => {
  const isDark = useDarkMode();
  const [viewMode, setViewMode] = useState<"map" | "grid">("map");
  const [globalFilter, setGlobalFilter] = useState("");
  const [isMapReady, setIsMapReady] = useState(geoMapRegistered);

  const cities = useMemo(() => collectCities(rows), [rows]);
  const regionTotals = useMemo(() => deriveRegionTotals(rows), [rows]);
  const regionCities = useMemo(() => deriveRegionCities(rows), [rows]);

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setGlobalFilter(e.target.value);
  };

  const handleExportCsv = () => {
    const filter = globalFilter.trim().toLowerCase();
    const filtered = filter
      ? regionTotals.filter((r) => r.region_name.toLowerCase().includes(filter))
      : regionTotals;
    const headers = ["Region", "Impressions", "Clicks", "CTR", "VCR"].join(",");
    const body = filtered
      .map((region) =>
        [
          CSV_ESCAPE(region.region_name),
          region.impressions,
          region.clicks,
          region.ctr.toFixed(2),
          region.video_complete_rate.toFixed(2),
        ].join(",")
      )
      .join("\n");

    const blob = new Blob([`\uFEFF${headers}\n${body}`], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "geographic-performance.csv";
    anchor.click();
    setTimeout(() => URL.revokeObjectURL(url), 100);
  };

  const topCities = useMemo(() => deriveTopCities(cities), [cities]);
  const marketCards = useMemo(() => deriveTopMarketCards(cities), [cities]);

  const mapOption = useMemo(
    () =>
      viewMode !== "map"
        ? null
        : buildGeoMapOption(regionTotals, isDark, regionCities),
    [viewMode, regionTotals, isDark, regionCities]
  );

  const barOption = useMemo(
    () => buildTopCitiesBarOption(topCities, isDark),
    [topCities, isDark]
  );

  useEffect(() => {
    if (!geoMapRegistered) {
      registerUsaMap();
      geoMapRegistered = true;
    }
    setIsMapReady(true);
  }, []);

  if (error) {
    return (
      <div className={`${styles.card} ${styles.fullRow}`}>
        <div className={styles.header}>
          <span className={styles.title}>{GEO_TEXT.title}</span>
        </div>
        <p className={styles.message}>{GEO_TEXT.error}</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <>
        <div className={styles.card}>
          <div className={styles.header}>
            <span className={styles.title}>{GEO_TEXT.title}</span>
          </div>
          <div
            aria-busy="true"
            className={styles.chartSkeleton}
            role="status"
          />
        </div>
        <div className={styles.card}>
          <div className={styles.header}>
            <span className={styles.title}>{GEO_TEXT.topCitiesTitle}</span>
          </div>
          <div className={styles.cardsRow}>
            {[0, 1].map((index) => (
              <div key={index} className={styles.summaryCard}>
                <Skeleton
                  height="0.75rem"
                  pt={{ root: { className: styles.skeleton } }}
                  width="55%"
                />
                <Skeleton
                  height="1.75rem"
                  pt={{ root: { className: styles.skeleton } }}
                  width="80%"
                />
              </div>
            ))}
          </div>
          <div
            aria-busy="true"
            className={styles.chartSkeleton}
            role="status"
          />
        </div>
      </>
    );
  }

  if (rows.length === 0) {
    return (
      <div className={`${styles.card} ${styles.fullRow}`}>
        <div className={styles.header}>
          <span className={styles.title}>{GEO_TEXT.title}</span>
        </div>
        <p className={styles.message}>{GEO_TEXT.noData}</p>
      </div>
    );
  }

  // The map and Top markets are sibling cards so the page grid can place them
  // at 3/5 + 2/5; they share the derived data above, which is why they live
  // in one component.
  return (
    <>
      <div className={styles.card}>
        <div className={styles.header}>
          <span className={styles.title}>{GEO_TEXT.title}</span>
          <ChartSwitcher
            label={TEXT.campaignOverview.switcher.view}
            options={VIEW_MODE_OPTIONS}
            value={viewMode}
            onChange={setViewMode}
          />
        </div>
        <div className={styles.mapContent}>
          {viewMode === "map" ? (
            // isMapReady gates EChart so registerUsaMap() always runs before the map
            // series is set — React fires children's effects before parents', so without
            // this guard the EChart useEffect would race registerUsaMap() on cache hits.
            // mapOption is non-null when viewMode === "map" (memo short-circuits otherwise)
            isMapReady && (
              <>
                <EChart option={mapOption!} />
                <div className={styles.scaleKey}>
                  <span>{GEO_TEXT.keyFewer}</span>
                  <span className={styles.scaleBar} />
                  <span>{GEO_TEXT.keyMore}</span>
                </div>
              </>
            )
          ) : (
            <GeoGrid
              globalFilter={globalFilter}
              regionTotals={regionTotals}
              onExportCsv={handleExportCsv}
              onFilterChange={handleFilterChange}
            />
          )}
        </div>
      </div>
      <div className={styles.card}>
        <div className={styles.header}>
          <span className={styles.title}>{GEO_TEXT.topCitiesTitle}</span>
        </div>
        <div className={styles.cardsRow}>
          <div className={styles.summaryCard}>
            <span className={styles.cardLabel}>
              {GEO_TEXT.cardTopImpressions}
            </span>
            {marketCards.topImpressionsCity ? (
              <>
                <span className={styles.cardValue}>
                  {COMPACT_NUMBER_FORMATTER.format(
                    marketCards.topImpressionsCity.impressions
                  )}
                </span>
                <span className={styles.cardCity}>
                  {cityLabel(marketCards.topImpressionsCity)}
                </span>
              </>
            ) : (
              <span className={styles.cardValue}>—</span>
            )}
          </div>
          <div className={styles.summaryCard}>
            <span className={styles.cardLabel}>{GEO_TEXT.cardTopCtr}</span>
            {marketCards.topCtrCity ? (
              <>
                <span className={styles.cardValue}>
                  {fmtPct(marketCards.topCtrCity.ctr ?? 0)}
                </span>
                <span className={styles.cardCity}>
                  {cityLabel(marketCards.topCtrCity)}
                </span>
              </>
            ) : (
              <span className={styles.cardValue}>—</span>
            )}
          </div>
        </div>
        <EChart
          option={barOption}
          style={{
            height: pillBarChartHeightRem(topCities.length),
            width: "100%",
          }}
        />
      </div>
    </>
  );
};

export default GeoWidget;
